import type { FC, ReactNode } from 'react'
import type { VideoCard as VideoCardData } from '@/types/content'

import Link from 'next/link'
import { Clock, Play } from 'lucide-react'
import { cms } from '@/lib/cms'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'
import { videoTypeLabels } from '@/lib/labels'
import { youtubeId, youtubeThumbnail } from '@/lib/youtube'
import { Picture } from '@/components/media/picture'
import { DemoBadge } from '@/components/site/demo-badge'

interface VideoCardProps {
    video: VideoCardData
    size?: 'md' | 'lg'
    className?: string
    priority?: boolean
}

export const VideoCard: FC<VideoCardProps> = ({ video, size = 'md', className, priority = false }): ReactNode => {
    const id = youtubeId(video.youtube_url)
    const poster = video.thumbnail ?? (id ? youtubeThumbnail(id) : null)

    return (
        <article className={cn('group', className)}>
            <Link href={`/videos/${video.slug}`} className='relative block aspect-video overflow-hidden rounded-md bg-charcoal ring-1 ring-white/[0.06] transition group-hover:ring-gold/30' aria-label={video.title}>
                {poster
                    ? <Picture src={poster} alt={video.thumbnail_alt ?? ''} grade='night' priority={priority} sizes={size === 'lg' ? '(min-width: 1024px) 60vw, 100vw' : '(min-width: 1024px) 30vw, 100vw'} imgClassName='transition duration-[1400ms] ease-cinematic group-hover:scale-[1.04] group-hover:brightness-110' />
                    : <div className='absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(114,43,59,0.4),transparent_70%)]' />}

                <div className='absolute inset-0 bg-gradient-to-t from-obsidian/85 via-obsidian/10 to-transparent' aria-hidden='true' />

                <span className={cn('absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-ivory/40 bg-obsidian/30 text-ivory backdrop-blur-md transition duration-500 ease-cinematic group-hover:scale-110 group-hover:bg-ivory group-hover:text-obsidian', size === 'lg' ? 'size-20' : 'size-14')} aria-hidden='true'>
                    {id ? <Play className={cn('translate-x-0.5 fill-current', size === 'lg' ? 'size-7' : 'size-5')} /> : <Clock className='size-5' />}
                </span>

                <span className='absolute left-4 top-4 flex items-center gap-2'>
                    <span className='rounded-full bg-obsidian/70 px-2.5 py-1 text-[0.6rem] font-medium uppercase tracking-[0.16em] text-ivory/85 backdrop-blur'>
                        {videoTypeLabels[video.video_type]}
                    </span>
                    {video.is_demo && <DemoBadge tone='overlay' />}
                </span>
            </Link>

            <div className='mt-4'>
                <h3 className={cn('font-heading leading-tight text-foreground', size === 'lg' ? 'text-3xl' : 'text-xl')}>
                    <Link href={`/videos/${video.slug}`} className='transition-colors hover:text-gold'>
                        <span {...cms('videos', video.id, 'title')}>{video.title}</span>
                    </Link>
                </h3>

                {size === 'lg' && video.description && (
                    <p className='mt-2 line-clamp-2 max-w-xl text-sm leading-relaxed text-muted-foreground'>
                        <span {...cms('videos', video.id, 'description')}>{video.description}</span>
                    </p>
                )}

                <p className='mt-2 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground'>
                    {video.song && (
                        <>
                            <span>From</span>
                            <Link href={`/music/${video.song.slug}`} className='text-foreground/80 hover:text-gold'>
                                <span {...cms('songs', video.song.id, 'title')}>{video.song.title}</span>
                            </Link>
                        </>
                    )}

                    {video.release_date && (
                        <>
                            {video.song && <span aria-hidden='true'>&middot;</span>}
                            <time dateTime={video.release_date}>{formatDate(video.release_date, 'short')}</time>
                        </>
                    )}
                </p>
            </div>
        </article>
    )
}
